<template>
  <div
    :id="fieldAnchorId"
    :class="[
      'v2-profile-field',
      {
        'v2-profile-field--disabled': !isInteractive,
        'v2-profile-field--error': error,
      },
    ]"
  >
    <label :for="inputId" class="v2-profile-field__label">
      {{ field.label || field.name || field.formattedName }}
      <span v-if="field.required" class="v2-profile-field__required" aria-hidden="true">*</span>
      <span
        v-if="editing && !isInteractive"
        class="v2-profile-field__readonly-badge"
        aria-label="Campo de solo lectura"
      >
        <v-icon size="12" aria-hidden="true">mdi-lock-outline</v-icon>
        Solo lectura
      </span>
    </label>

    <!-- TEXT input -->
    <BaseInput
      v-if="isText"
      :id="inputId"
      :value="field.value || ''"
      :type="field.type"
      :placeholder="field.placeholder || ''"
      :disabled="!isInteractive"
      :required="field.required"
      @input="onUpdate($event)"
    />

    <!-- TEXTAREA for long text -->
    <BaseTextarea
      v-else-if="isLongText"
      :id="inputId"
      :value="field.value || ''"
      :placeholder="field.placeholder || ''"
      :disabled="!isInteractive"
      :required="field.required"
      :error="error"
      :rows="4"
      @input="onUpdate($event)"
    />

    <!-- LIST / select -->
    <BaseSelect
      v-else-if="isList"
      :id="inputId"
      :value="field.value || ''"
      :options="selectOptions"
      :disabled="!isInteractive"
      :required="field.required"
      :placeholder="field.placeholder || 'Selecciona…'"
      @change="onUpdate($event)"
    />

    <!-- DATE -->
    <input
      v-else-if="isDate"
      :id="inputId"
      type="date"
      class="v2-input"
      :value="dateValue"
      :disabled="!isInteractive"
      :required="field.required"
      @input="onUpdate($event.target.value)"
    />

    <!-- READ-ONLY (DOC, IMAGE, unsupported) -->
    <div
      v-else
      class="v2-profile-field__readonly"
    >
      {{ field.value || 'N/D' }}
    </div>

    <p v-if="error" class="v2-profile-field__error">{{ error }}</p>
    <p v-else-if="field.helper" class="v2-profile-field__helper">{{ field.helper }}</p>
  </div>
</template>

<script>
import BaseInput from "@/components/memberDashboardV2/ui/BaseInput.vue";
import BaseSelect from "@/components/memberDashboardV2/ui/BaseSelect.vue";
import BaseTextarea from "@/components/memberDashboardV2/ui/BaseTextarea.vue";

let counter = 0;

export default {
  name: "ProfileField",
  components: { BaseInput, BaseSelect, BaseTextarea },
  props: {
    field: { type: Object, required: true },
    editing: { type: Boolean, default: false },
    error: { type: String, default: "" },
  },
  data() {
    counter += 1;
    return { _id: `v2-pf-${counter}` };
  },
  computed: {
    inputId() {
      return `${this._id}-${this.field.key || this.field._id || 'field'}`;
    },
    fieldAnchorId() {
      const key = this.field._renderKey || this.field.key || this.field.formattedName || this.field._id || "field";
      return `v2-profile-field-${String(key).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
    },
    isText() {
      return this.field.type === "text" || this.field.type === "email" || this.field.type === "tel" || this.field.type === "number";
    },
    isLongText() {
      // For now, treat every TEXT field as a regular input. We can switch
      // to a textarea based on a hint flag from the field config later.
      return false;
    },
    isList() {
      return this.field.type === "list" && this.field.options && this.field.options.length > 0;
    },
    isDate() {
      return this.field.type === "date";
    },
    selectOptions() {
      const opts = this.field._dynamicOptions || this.field.options || [];
      return opts.map((opt) => ({ value: opt, label: opt }));
    },
    dateValue() {
      if (!this.field.value) return "";
      const d = new Date(this.field.value);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    },
    isInteractive() {
      if (!this.editing) return false;
      if (this.field.editable === false) return false;
      return true;
    },
  },
  methods: {
    onUpdate(value) {
      this.$emit("update", { key: this.field.key || this.field.formattedName, value, isStatic: this.field.isStatic });
    },
  },
};
</script>

<style scoped>
.v2-profile-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.v2-profile-field--disabled {
  opacity: 0.85;
}

.v2-profile-field--error {
  border: 1px solid var(--color-error-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  background-color: var(--color-error-soft);
  box-shadow: 0 0 0 3px var(--color-error-soft-2), inset 3px 0 0 var(--color-error);
  transition: background-color var(--transition-fast), box-shadow var(--transition-fast);
}

.v2-profile-field--error .v2-profile-field__label {
  color: var(--color-error);
}

.v2-profile-field__label {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-1);
  font-family: var(--font-body);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
}

.v2-profile-field__readonly-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background-color: var(--color-neutral-100);
  color: var(--color-text-muted);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: normal;
  line-height: 1.2;
  text-transform: none;
  white-space: nowrap;
}

.v2-profile-field__required {
  color: var(--color-error);
  margin-left: 2px;
}

.v2-profile-field__readonly {
  padding: var(--space-2) var(--space-3);
  background-color: var(--color-neutral-50);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  font-size: var(--fs-body-sm);
  min-height: 40px;
  display: flex;
  align-items: center;
}

.v2-profile-field__helper {
  margin: 0;
  font-size: var(--fs-label-xs);
  color: var(--color-text-muted);
}

.v2-profile-field__error {
  margin: 0;
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-error);
}
</style>
